/* eslint-disable sonarjs/no-duplicate-string */
import { html } from "common-tags"

import { UserAccount } from "../UserAccount"
import { isBrowser } from "../infrastructure/ssg-utils"
import { hashify } from "../hashify"
import { Psychologist } from "../interfaces"

import {
    fbCustom,
    fbInitiateCheckout,
    fbViewNamedContent,
    fbViewProductContent,
} from "./facebook-pixel"
import {
    digest,
    EventDesc,
    interaction,
    metadata,
    nonInteraction,
    pageview,
    ReadyEventDesc,
    setUpAnalyticsObject,
} from "./funnel-utils"
import { ClientAccountAnalytics } from "./client-account"

import { isClientAccountPage, isPsyAccountPage } from "@app/AppRouter/utils"

const event_value = true
const event_label = true
const fb_content_id = true
const data_layer_variable = true

export const GA_CLIENT_ID_DIMENSION = "dimension9"

export const Attributes = metadata({
    count: {
        title: "Подсчет событий, всегда репортится eventValue=1",
        compute: () => 1,
        event_value,
    },
    psychologist: {
        title: "Психолог",
        format: "Имя Ф. (ID в базе)",
        ga_id: "dimension2",
        compute: (d: { psy: Psychologist }) => `${d.psy.display_name} (${d.psy.hash_id})`,
        event_label,
        fb_content_id,
    },
    position_in_results: {
        title: "Позиция в выдаче, начиная с 1",
        ga_id: ["metric1", "dimension10"],
        compute: (d: { position: number }) => d.position,
        event_value,
    },
    city: {
        title: "Город, выбранный в анкете/каталоге",
        format: "Москва|Санкт-Петербург|other_city",
        ga_id: "dimension4",
        compute: (d: { city: string }) => d && d.city,
    },
    formOfWork: {
        title: "Форма работы, выбранная в анкете/каталоге",
        format: "online|in_person|any",
        ga_id: "dimension5",
        compute: (d?: { form_of_work: string }) =>
            d && "form_of_work" in d ? d.form_of_work : undefined,
    },
    haveBeenBefore: {
        title: "Отметка о том, что пользователь ранее посещал психолога, выбранная в анкете",
        format: "yes|no",
        ga_id: "dimension20",
        compute: (d?: { have_been_before: string }) =>
            d && "have_been_before" in d ? d.have_been_before : undefined,
    },
    priceCategory: {
        title: "Ценовая категория, выбранная в анкете",
        format: "0_2500|2500_3500|3500_5000|5000_|any",
        compute: (d?: { price_category: string }) =>
            d && "price_category" in d ? d.price_category : undefined,
        event_label,
    },
    maritalStatus: {
        title: "Семейное положение, выбранное в каталоге",
        format: "married|single|any",
        ga_id: "dimension12",
        compute: (d?: { marital_status: string }) =>
            d && "marital_status" in d ? d.marital_status : undefined,
    },
    sex: {
        title: "Пол, выбранный в каталоге",
        format: "female|male|any",
        ga_id: "dimension13",
        compute: (d?: { sex: string }) => (d && "sex" in d ? d.sex : undefined),
    },
    has_children: {
        title: "Наличие детей, выбранное в каталоге",
        format: "yes|no|any",
        ga_id: "dimension14",
        compute: (d?: { has_children: string }) =>
            d && "has_children" in d ? d.has_children : undefined,
    },
    subject: {
        title: "Кто будет общаться с психологом (ответ из анкеты)",
        format: "myself|family|child",
        ga_id: "dimension6",
        compute: (d?: { subject: string }) => d && d.subject,
    },
    problem_categories: {
        title: "Какие категории проблем выбраны в анкете (для случая Только я)",
        format: "через запятую",
        ga_id: "dimension18",
        compute: (d: { myself_personal: string[] }) =>
            d && d.myself_personal && d.myself_personal.join(","),
    },
    problem_categories_number: {
        title: "Сколько категорий проблем выбраны в анкете (для случая Только я)",
        format: "число",
        event_value,
        compute: (d?: { myself_personal: string[] }) =>
            d && d.myself_personal && d.myself_personal.length,
    },
    child_problem_categories: {
        title: "Какие категории проблем выбраны в анкете (для случая Ребенок)",
        format: "через запятую",
        ga_id: "dimension18",
        compute: (d?: { child: string[] }) => d && d.child && d.child.join(","),
    },
    child_problem_categories_number: {
        title: "Сколько категорий проблем выбраны в анкете (для случая Ребенок)",
        format: "число",
        event_value,
        compute: (d?: { child: string[] }) => d && d.child && d.child.length,
    },
    priceFrom: {
        title: "Мин. цена, выбранная в каталоге",
        ga_id: "metric4",
        compute: (d: { price_from: number }) => d.price_from || undefined,
    },
    priceTo: {
        title: "Макс. цена, выбранная в каталоге",
        ga_id: "metric2",
        compute: (d: { price_to: number }) => (d.price_to >= 20000 ? undefined : d.price_to),
    },
    ageFrom: {
        title: "Мин. возраст, выбранный в каталоге",
        ga_id: "metric5",
        compute: (d: { age_from: number }) => (d.age_from <= 18 ? undefined : d.age_from),
    },
    ageTo: {
        title: "Макс. возраст, выбранный в каталоге",
        ga_id: "metric6",
        compute: (d: { age_to: number }) => (d.age_to >= 100 ? undefined : d.age_to),
    },
    auto_sent: {
        title: "Письмо было оправлено автоматически",
        format: "1 - да, 0 - нет",
        ga_id: "metric3",
        compute: (d: { sentAutomatically: number }) =>
            d && "sentAutomatically" in d ? d.sentAutomatically : undefined,
    },
    question: {
        title: "Текст вопроса",
        compute: (q: { name?: string; title: string }) => (q && "name" in q ? q.name : q.title),
        event_label,
    },
    rating: {
        title: "Сколько очков пользователь поставил данному вопросу/варианту ответа",
        format: "число",
        compute: (q: { value: string }) => parseInt(q.value, 10),
        event_value,
    },
    top_competence: {
        title: "Главная компетенция, выделенная по анкете",
        format:
            "Репортится одна главная компетенция, если она есть. Если нет (не удалось выделить одну), " +
            'репортится фраза "Не удалось выделить одну компетенцию"',
        compute(d?: { top_competence?: string }) {
            return d && "top_competence" in d
                ? d.top_competence
                : "Не удалось выделить одну компетенцию"
        },
        event_label,
    },
    email_hash: {
        title: "Уникальный хэш email'а пользователя",
        format: "SHA1 от адреса, написанного маленькими буквами",
        ga_id: "dimension7",
        compute(d?: { email?: string }) {
            const email = d && "email" in d ? d.email : (UserAccount.email as string)
            if (!email) return
            return hashify(email.toLowerCase())
        },
    },
    email_provider: {
        title: "Почтовый провайдер пользователя",
        format: "Часть email-адреса после @",
        ga_id: "dimension8",
        compute(d?: { email?: string }) {
            const email = d && "email" in d ? d.email : (UserAccount.email as string)
            if (!email) return
            return email.substring(email.indexOf("@") + 1)
        },
    },
    results_page: {
        title: "Страница в выдаче",
        format: "Номер, начиная с 1",
        compute: (d: { page: number }) => d.page + 1,
        event_value,
        event_label,
    },
    booking_id: {
        title: "ID бронирования",
        compute: (d: { booking_id: string }) => d.booking_id,
        event_label,
    },
    user_followed_the_link_to_social_network: {
        title: "Пользователь перешел по ссылке в социальную сеть",
        compute: (d: { socialNetwork: string }) => d.socialNetwork,
        event_label,
    },
    recommended_block_user_followed_the_link_to_social_network: {
        title: "Название соц. сети",
        compute: (d: { socialNetwork: string }) => d.socialNetwork,
        event_label,
    },
    psychologist_service: {
        title: "Услуга психолога",
        compute: (d: { service_type: string }) => d.service_type,
        event_label,
    },
    profile_skills_tab_id: {
        title: "ID вкладки в разделе квалификация на странице профиля",
        ga_id: "dimension17",
        format: "education|methods|features|expertise",
        compute: (d: { tabId: string }) => d.tabId,
    },
    context_menu_page: {
        title: "Страница, на которой было открыто контекстное меню: профиль психолога или бронирование",
        format: "profile|booking|survey_card|catalog_card",
        event_label,
        compute: (d: { from: string }) => d.from,
    },
    type_user: {
        title: "Кто создал событие",
        format: "client|psychologist",
        event_label,
        compute: () => {
            if (isClientAccountPage()) {
                return "client"
            }
            if (isPsyAccountPage()) {
                return "psychologist"
            }
        },
    },
    user_name_email_object: {
        title: "Данные пользователя: имя, email",
        compute: (d: { name: string; email: string }) => d,
        data_layer_variable,
    },
    user_booking_details: {
        title: "Данные заказа: имя, email, id заказа, цена",
        compute: (d: { name: string; email: string; orderId: number; price: string }) => d,
        data_layer_variable,
    },
    experiment_id: {
        title: "ID эксперимента в GrowthBook",
        compute: (d: { experimentId: string }) => d.experimentId,
    },
    variation_id: {
        title: "ID варианта эксперимента в GrowthBook",
        compute: (d: { variationId: string }) => d.variationId,
    },
    survey_type: {
        title: "Тип анкеты",
        format: "b2b|b2c",
        compute: (d: { isB2b: boolean }) => (d.isB2b ? "b2b" : "b2c"),
    },
    survey_name: {
        title: "Название анкеты",
        compute: (d: { surveyName: string }) => d.surveyName,
    },
    prev_survey_question_id: {
        title: "Прерыдущий ID вопроса анкеты",
        compute: (d: { prevQuestionId?: string }) => d.prevQuestionId,
    },
    survey_question_id: {
        title: "ID вопроса анкеты",
        compute: (d: { questionId: string }) => d.questionId,
    },
    survey_page_number: {
        title: "Страница анкеты",
        compute: (d: { pageNumber: number }) => d.pageNumber,
    },
    survey_answer: {
        title: "Ответ Анкеты",
        compute: (d: { answer?: string }) => d.answer,
    },
})

const searchCriteria = [
    Attributes.city,
    Attributes.formOfWork,
    Attributes.priceFrom,
    Attributes.priceTo,
    Attributes.maritalStatus,
    Attributes.sex,
    Attributes.has_children,
    Attributes.ageFrom,
    Attributes.ageTo,
]
const Aggregates = {
    user: { data: [Attributes.email_hash, Attributes.email_provider] },
    psychologist: { data: [Attributes.psychologist, Attributes.position_in_results] },
    email: {
        data: [Attributes.psychologist, Attributes.position_in_results, Attributes.auto_sent],
    },
    basic_questions: { data: [Attributes.city, Attributes.formOfWork, Attributes.subject] },
    search_criteria: { data: [...searchCriteria, digest("eventLabel", ...searchCriteria)] },
    booking_data: { data: [Attributes.booking_id, Attributes.psychologist] },
    psychologist_service_data: { data: [Attributes.psychologist, Attributes.psychologist_service] },
}

const viewprofile = pageview({
    title: "Открыли профиль психолога",
    explanation: html`
        Пользователь нажал "Подробнее" на карточке одного из психологов. После этого он может
        отправить психологу сообщение или вернуться к выдаче
    `,
    ...Aggregates.psychologist,
    ...fbViewProductContent,
})

const booking_dialog = pageview({
    title: "Открыли диалог онлайн-бронирования",
    ...Aggregates.email,
    ...fbCustom,
})

const booking_dialog_free = pageview({
    title: "Открыли диалог бесплатного онлайн-бронирования",
    ...Aggregates.email,
    ...fbCustom,
})

const psychologist_service_selected = interaction({
    title: "Выбрали услугу и согласились с условиями",
    explanation: html`
        Клиент выбрал услугу, согласился с условиями оферты и переходит к бронированию
    `,
    ...Aggregates.psychologist_service_data,
})

const user_followed_the_link_to_social_network = nonInteraction({
    title: "Пользователь перешел по ссылке в социальные сети",
    explanation: html` Пользователь нажал на иконку социальной сети в футере `,
    data: [Attributes.user_followed_the_link_to_social_network],
})

const recommended_block_user_followed_the_link_to_social_network = nonInteraction({
    title: "Пользователь поделился ссылкой на социальную сеть",
    explanation: html` Пользователь нажал на иконку социальной сети в блоке "Поделитесь с близкими"`,
    data: [Attributes.recommended_block_user_followed_the_link_to_social_network],
})

const psychologist_service_changed_on_click = interaction({
    title: "Переключили услугу",
    explanation: html` Клиент переключил услугу через панель над календарем `,
})

const form_of_work_switched = interaction({
    title: "Переключили услугу между 'Только вы' и 'С партнером или семьей'",
    explanation: html` Клиент переключил тип услуги с помощью 'карточек' `,
})

const session_time_selected = interaction({
    title: "Выбрали время сессии",
    explanation: html`Клиент выбрал время сессии в календаре и перешел на экран подтверждения`,
})

const session_time_selected_free = interaction({
    title: "Выбрали время бесплатной сессии",
    explanation: html`Клиент выбрал время бесплатной сессии в календаре и перешел на экран
    подтверждения`,
})

const session_details_confirmed = interaction({
    title: "Подтвердили детали бронирования",
    explanation: html` Клиент подтвердил детали бронирования, нажав на кнопку "Забронировать" `,
})

const successful_booking_page_shown = interaction({
    title: "Увидели страницу с приглашением к оплате",
    explanation: html`
        Клиент увидел страницу с сообщением, что время забронировано. В нормальной ситуации после
        этого сразу открывается виджет оплаты. Если не открывается, то есть кнопка "Оплатить"
    `,
})

const successful_payment_page_shown = interaction({
    title: "Увидели страницу с сообщением об успешной оплате",
    explanation: html` Клиент увидел страницу с сообщением, что сессия успешно оплачена. `,
})

const free_slot_too_far_away = nonInteraction({
    title: "В календаре психолога в ближайшее время нет свободных слотов",
    explanation: html`
        При попытке забронировать сессию с психологом из выдачи клиент не увидел свободных слотов в
        ближайшее время
    `,
    ...Aggregates.psychologist,
})

const no_free_slots = nonInteraction({
    title: "В календаре психолога нет свободных слотов",
    explanation: html`
        При попытке забронировать сессию с психологом из выдачи клиент не увидел ни одно свободного
        слота
    `,
    ...Aggregates.psychologist,
})

const send_email = pageview({
    title: "Отправили сообщение психологу (основное)",
    explanation: html`
        Если сработало автописьмо (мы в анкете и пользователь оставил свой email раньше), то это
        событие репортится на автописьмо, а на последующиее письмо тому же психологу своими словами
        — не репортится Если автописьмо не было отправлено, то это событие репортится на письмо,
        написанное вручную так всегда происходит в каталоге, например
    `,
    ...Aggregates.email,
    ...fbCustom,
})

const email_dialog_cancel = interaction({
    title: "Не стали отправлять описание своими словами",
    explanation: html`
        Пользователь закрыл диалог отправки сообщения с запросом/предпочтениями по времени, не
        отправляя сообщение, написанное своими словами
    `,
    ...Aggregates.email,
})

const in_search_results = nonInteraction({
    title: "Психолог показан в выдаче",
    ...Aggregates.psychologist,
})

const psychologists_loaded = pageview({
    title: "Увидели выдачу",
    ...Aggregates.basic_questions,
    ...fbViewNamedContent,
})

const after_successful_booking = interaction({
    title: "Бронирование успешно завершено",
    explanation: html`Клиент забронировал время сессии и переходит к оплате`,
    ...Aggregates.booking_data,
    ...fbInitiateCheckout,
})

const after_successful_booking_free = interaction({
    title: "Бесплатное бронирование успешно завершено",
    explanation: html`Клиент забронировал бесплатное время сессии и переходит к оплате`,
    ...Aggregates.booking_data,
    ...fbInitiateCheckout,
})

const payment_widget_shown = interaction({
    title: "Перешли на платежный виджет (W1 или CP)",
    explanation: html`
        Клиент перешел к оплате (результат завершения оплаты пока не известен) Это может произойти
        либо автоматически, либо по нажатию кнопки "Оплатить"
    `,
    ...Aggregates.booking_data,
})

/*
@warning: after_payment now writes from backend

const after_payment = interaction({
    title: 'Клиент оплатил сессию',
    ...Aggregates.booking_data,
})
*/

const payment_failed = interaction({
    title: "Неуспешная попытка оплаты сессии",
    ...Aggregates.booking_data,
})

const profile_skills_tab_selected = interaction({
    title: "Пользователь выбрал вкладку в разделе квалицификация",
    data: [Attributes.profile_skills_tab_id],
})

const profile_show_tooltip = interaction({
    title: "Пользователь нажал на иконку вопроса и увидел тултип",
})

// We need this to be a 'let' to provide a safe proxy and avoid exceptions in production
// while keeping navigation from the call sites clean in the IDE
export let Analytics = {
    survey_loaded: pageview({
        title: "Зашли в анкету",
        data: [Attributes.survey_type],
    }),
    survey_started: pageview({
        title: "Ответили на первый вопрос в анкете",
        explanation: html` Пользователь нажал на какой-то вариант ответа в первом вопросе анкеты `,
        data: [Attributes.survey_type],
    }),
    survey_finished: pageview({
        title: "Прошли анкету до конца",
        explanation: html`
            Пользователь ответил на последний вопрос (до формы сбора email), и ему доступна кнопка
            "Далее", которая ведет на выдачу (возможно, через регистрацию)
        `,
        data: [Attributes.survey_type],
    }),
    survey_results: pageview({
        title: "Перешли на страницу с результатами анкеты",
        explanation: html` Пользователь перешел на страницу выдачи. `,
        data: [Attributes.survey_type],
    }),
    event_survey_answer: interaction({
        title: "Клиент ответил в анкете",
        data: [
            Attributes.survey_type,
            Attributes.survey_name,
            Attributes.survey_page_number,
            Attributes.survey_question_id,
            Attributes.survey_answer,
        ],
    }),
    event_survey_return: interaction({
        title: "Клиент вернулся к предыдущему вопросу в анкете",
        data: [
            Attributes.survey_type,
            Attributes.survey_name,
            Attributes.prev_survey_question_id,
            Attributes.survey_question_id,
        ],
    }),
    signup_form: pageview({
        title: "Увидели форму регистрации",
        explanation: html`
            Если пользователь отправит свои регистрационные данные, он перейдет на выдачу
        `,
        ...Aggregates.basic_questions,
    }),
    psychologists_loaded,
    email_dialog: pageview({
        title: 'Открыли диалог "Написать сообщение"',
        explanation: html`
            Пользователь нажал "Отправить сообщение". При этом мы сразу отправили письмо с данными
            его анкеты психологу и показали диалог, где можно описать запрос и предпочтения по
            времени своими словами
        `,
        ...Aggregates.email,
        ...fbCustom,
    }),
    booking_dialog,
    booking_dialog_free,
    send_extra_email: pageview({
        title: "После автоматического письма отправили письмо вручную",
        ...Aggregates.email,
    }),
    send_email,
    viewprofile,
    profile_skills_tab_selected,
    profile_show_tooltip,
    back_to_survey: pageview({
        title: "Вернулись из выдачи обратно в анкету",
        explanation: html`
            Пользователь нажал "Назад" на странице с выдачей и вернулся к анкете, где он может
            менять свои ответы
        `,
    }),
    email_dialog_cancel,
    prev_survey_page: interaction({
        title: "Вернулись назад внутри анкеты",
        explanation: html` Пользователь нажал "Назад" на одной из страниц анкеты (не в выдаче) `,
    }),
    in_search_results,
    send_questionnaire_results: nonInteraction({
        title: "Мы отправили клиенту email с выдачей",
        ...Aggregates.basic_questions,
    }),
    psy_card_close_clicked: interaction({
        title: "Нажал крестик на карточке",
        explanation: html` Пользователь нажал крестик на карточке психолога в выдаче `,
    }),
    replace_failed: nonInteraction({
        title: "Не удалось найти замену",
        explanation: html`
            Пользователь нажал кнопку "заменить" и замену психологу найти не удалось
        `,
    }),
    go_to_search: interaction({
        title: "Перешли из анкеты в каталог",
        explanation: html` Пользователь нажал кнопку "Каталог психологов" на странице с выдачей `,
        ...Aggregates.basic_questions,
    }),
    answer: nonInteraction({
        title: "Ответ пользователя на один вопрос анкеты",
        explanation: html`
            Для радиогрупп: "имя вопроса:имя ответа" -> 1 Для чекбоксов: "имя вопроса:текст ответа"
            -> 1 Для рейтингов (шкал): "текст вопроса" -> число баллов Нулевые значения не
            репортятся
        `.split("\n"),
        data: [Attributes.question, Attributes.rating, ...Aggregates.basic_questions.data],
    }),
    top_competence: nonInteraction({
        title: "Главная компетенция, выделенная по анкете",
        data: [Attributes.top_competence, Attributes.count, ...Aggregates.basic_questions.data],
    }),
    show_more_clicked: interaction({
        title: "Показать еще в выдаче",
        explanation: html` Пользователь нажал показать еще в выдаче `,
    }),
    expand_competence_list_in_card: interaction({
        title: 'Нажали "Еще" в карточке психолога в выдаче',
        explanation: html`
            Пользователь развернул список специализаций в карточке психолога в выдаче
        `,
    }),
    collapse_competence_list_in_card: interaction({
        title: 'Нажали "Свернуть" в карточке психолога в выдаче',
        explanation: html`
            Пользователь свернул список специализаций в карточке психолога в выдаче
        `,
    }),
    guarantees_message_shown: interaction({
        title: 'Нажали на "Расширенные гарантии от Alter"',
        explanation: html` Пользователь кликнул на ссылку "Расширенные гарантии от Alter" `,
    }),
    psychologist_service_selected,
    user_followed_the_link_to_social_network,
    recommended_block_user_followed_the_link_to_social_network,
    psychologist_service_changed_on_click,
    form_of_work_switched,
    session_time_selected,
    session_time_selected_free,
    session_details_confirmed,
    successful_booking_page_shown,
    successful_payment_page_shown,
    free_slot_too_far_away,
    no_free_slots,
    after_successful_booking,
    after_successful_booking_free,
    payment_widget_shown,
    // after_payment,
    payment_failed,
    search_form_advanced_shown: interaction({
        title: "Показаны расширенные критерии поиска",
        ...Aggregates.search_criteria,
    }),
    search: nonInteraction({
        title: "Поиск по каталогу",
        ...Aggregates.search_criteria,
    }),
    search_by_session_time_intervals: nonInteraction({
        title: "Поиск по каталогу по времени сессии",
        data: [
            {
                title: "Время сессии",
                format: "время в таймзоне клиента|время в UTC",
                event_label,
                compute: <
                    Type extends {
                        sessionTimeInterval: { begin: string; begin_utc: string }
                    } & EventDesc,
                >(
                    d: Type
                ) => `${d.sessionTimeInterval.begin}|${d.sessionTimeInterval.begin_utc}`,
            },
        ],
    }),
    empty_search_result: nonInteraction({
        title: "Пользователь получил пустую выдачу",
        ...Aggregates.search_criteria,
    }),
    page_shown: nonInteraction({
        title: "Пользователь выбрал страницу в выдаче поиска (явным кликом)",
        data: [...Aggregates.search_criteria.data, Attributes.results_page],
    }),
    session_moved_without_refund: interaction({
        title: "Сессия перенесена без возврата денег",
        explanation: html`
            Клиент перенес сессию менее чем за 24 часа до начала Строго говоря отменил текущую и
            забронировал новую
        `,
    }),
    to_profile_clicked: nonInteraction({
        title: "Нажал на кнопку Подробнее на экране выдачи",
        explanation: html` Пользователь нажал на кнопку Подробнее на экране выдачи `,
    }),
    stories_navigation_clicked: nonInteraction({
        title: "Нажал на stories-навигацию в профиле психолога",
        explanation: html` Пользователь нажал на stories-навигацию в профиле психолога `,
    }),
    send_email_success: nonInteraction({
        ...Aggregates.email,
    }),
    send_email_error: nonInteraction({
        ...Aggregates.email,
    }),
    send_client_signup: nonInteraction({}),
    client_signup_error: nonInteraction({}),
    // Statuses
    initial: nonInteraction({}),
    in_survey: nonInteraction({}),
    in_survey_form: nonInteraction({}),
    survey_submitted: nonInteraction({}),
    psychologists_fetched: nonInteraction({}),
    no_psychologists_found: nonInteraction({}),
    survey: nonInteraction({}),
    not_found_page: nonInteraction({}),
    profile: nonInteraction({}),
    search_page: nonInteraction({}),
    results_from_email: nonInteraction({}),
    list: nonInteraction({}),
    booking: nonInteraction({}),
    funnel_docs: nonInteraction({}),
    error: nonInteraction({}),
    payment: nonInteraction({}),
    psychologist_account: nonInteraction({}),
    booking_details: nonInteraction({
        title: "Данные заказа",
        data: Attributes.user_booking_details,
    }),
    free_psys_page: nonInteraction({}),
    // data layer events
    booking_details_shown: nonInteraction({}),
    booking_widget_shown: nonInteraction({
        title: "Пользователь перешел на страницу выбора даты и времени сессии",
    }),
    singup_form_shown: nonInteraction({}),
    questionnaire_choice_city: nonInteraction({
        data: [Attributes.city],
    }),
    questionnaire_choice_form_of_work: nonInteraction({
        data: [Attributes.formOfWork],
    }),
    questionnaire_have_been_before: nonInteraction({
        data: [Attributes.haveBeenBefore],
    }),
    questionnaire_choice_subject: nonInteraction({
        data: [Attributes.subject],
    }),
    questionnaire_choice_price: nonInteraction({
        data: [Attributes.priceCategory],
    }),
    questionnaire_choice_problem_categories: nonInteraction({
        data: [Attributes.problem_categories, Attributes.problem_categories_number],
    }),
    questionnaire_choice_child_problem_categories: nonInteraction({
        data: [Attributes.child_problem_categories, Attributes.child_problem_categories_number],
    }),
    tobook_clicked: nonInteraction({
        data: [
            {
                title: "Где нажата кнопка: в выдаче или в профиле",
                format: "card|main|middle|bottom",
                event_label,
                compute: <Type extends { from: string } & EventDesc>(d: Type) => d.from,
            },
        ],
    }),
    context_menu_opened_while_psy_selected: nonInteraction({
        title: "Пользователь открыл контекстное меню, выделив имя или фото психолога",
        data: [Attributes.context_menu_page],
    }),
    free_slot: nonInteraction({
        title: "Пользователь увидел свободный слот в календаре",
    }),
    free_slots_3: nonInteraction({
        title: "Пользователь от 1-го до 4-х свободных слотов в календаре",
    }),
    free_slots_5: nonInteraction({
        title: "Пользователь от 5-ти до 7-ми свободных слотов в календаре",
    }),
    free_slots_8: nonInteraction({
        title: "Пользователь увидел более 8-ми свободных слотов в календаре",
    }),
    ask_open_slot_clicked: nonInteraction({
        title: "Пользователь кликнул 'Запросить свободное время' на странице выбора слота в календаре",
    }),
    ask_open_slot_email_sent_from_booking: nonInteraction({
        title: "Пользователь кликнул 'Запросить свободное время' на странице выбора слота в календаре и отправил сообщение психологу",
    }),
    psy_collection: interaction({
        title: "Пользователь открыл подборку психологов",
    }),
    audio_listening_started: nonInteraction({
        title: "Пользователь начал прослушивание аудио",
        data: [Attributes.psychologist],
    }),
    audio_listened_10seconds: nonInteraction({
        title: "Пользователь прослушал 10 секунд аудио",
        data: [Attributes.psychologist],
    }),
    audio_listened_30seconds: nonInteraction({
        title: "Пользователь прослушал 30 секунд аудио",
        data: [Attributes.psychologist],
    }),
    audio_listening_stopped: nonInteraction({
        title: "Пользователь остановил прослушивание аудио",
        data: [
            Attributes.psychologist,
            {
                title: "Время остановки прослушивания",
                format: "[секунды]",
                compute: <Type extends { time: number } & EventDesc>(d: Type) => d.time,
                event_label,
            },
        ],
    }),
    client_authorization: nonInteraction({}),
    gadv_after_first_payment: nonInteraction({}),
    gadv_client_signup: nonInteraction({}),
    go_to_my_alter: nonInteraction({
        title: "Клиент нажал на Мой Альтер в главном меню",
    }),
    go_to_sessions: nonInteraction({
        title: "Клиент нажал на Сессии в главном меню",
    }),
    go_to_selections: nonInteraction({
        title: "Клиент нажал на Подборки в главном меню",
    }),
    go_to_selections_from_account: nonInteraction({
        title: "Клиент нажал на Подборки в личном кабинете",
    }),
    ...ClientAccountAnalytics,
    similar_psy_profile_clicked: nonInteraction({
        title: "Пользователь перешёл в профиль психолога через блок с 'похожими' психологами в профиле психолога после после выдачи",
    }),
    support_tooltip_email_clicked: nonInteraction({
        title: "Пользователь кликнул на email в блоке неуспешной оплаты",
    }),
    support_tooltip_telegram_clicked: nonInteraction({
        title: "Пользователь кликнул на telegram в блоке неуспешной оплаты",
    }),
    certificate_block_button_clicked: nonInteraction({
        title: "Пользователь кликнул на кнопку в блоке сертификат",
    }),
    open_time_request_modal: nonInteraction({
        title: "Пользователь открыл модальное окно 'Запросить время'",
    }),
    close_time_request_modal: nonInteraction({
        title: "Пользователь закрыл модальное окно 'Запросить время'",
    }),
    choose_time_time_request_modal: nonInteraction({
        title: "Пользователь выбрал время: в модальном окне 'Запросить время'",
        data: [
            {
                title: "Время сессии",
                format: "время в таймзоне клиента",
                event_label,
                compute: <Type extends { time: string } & EventDesc>(d: Type) => d.time,
            },
        ],
    }),
    open_time_request_no_slots_modal: nonInteraction({
        title: "Пользователь открыл модальное окно 'Запросить время' без слотов",
    }),
    close_time_request_no_slots_modal: nonInteraction({
        title: "Пользователь закрыл модальное окно 'Запросить время' без слотов",
    }),
    choose_time_time_request_no_slots_modal: nonInteraction({
        title: "Пользователь выбрал время: в модальном окне 'Запросить время' без слотов",
        data: [
            {
                title: "Время сессии",
                format: "время в таймзоне клиента",
                event_label,
                compute: <Type extends { time: string } & EventDesc>(d: Type) => d.time,
            },
        ],
    }),
    user_went_to_calendar: nonInteraction({
        title: "Пользователь перешел на страницу выбора даты и времени сессии",
    }),
    user_went_to_form: nonInteraction({
        title: "Пользователь перешел на форму подтверждения данных",
    }),
    user_went_to_form_after_time_request_modal: nonInteraction({
        title: "Пользователь перешел на форму после модального окна 'Запросить время'",
    }),
    user_went_to_payment_after_time_request_modal: nonInteraction({
        title: "Пользователь перешел на оплату после модального окна 'Запросить время'",
    }),
    user_went_to_payment_success_after_time_request_modal: nonInteraction({
        title: "Пользователь перешел на успешную оплату после модального окна 'Запросить время'",
        data: Attributes.user_booking_details,
    }),
    user_went_to_form_after_time_request_no_slots_modal: nonInteraction({
        title: "Пользователь перешел на форму после модального окна 'Запросить время' без слотов",
    }),
    user_went_to_payment_after_time_request_no_slots_modal: nonInteraction({
        title: "Пользователь перешел на оплату после модального окна 'Запросить время' без слотов",
    }),
    user_click_on_button_in_sign_up_again_card: nonInteraction({
        title: "Пользователь кликнул на кнопку в карточке «Записаться еще раз»",
    }),
    user_click_on_psy_profile_in_sign_up_again_card: nonInteraction({
        title: "Пользователь кликнул на профиль психолога в карточке «Записаться еще раз»",
    }),
    video: nonInteraction({}),
    before_primary_action_connect: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Подключиться к звонку'",
        data: Attributes.type_user,
    }),
    before_sessions_started_connect: nonInteraction({
        title: "Пользователь кликнул на баннер 'До начала сессии меньше'",
        data: Attributes.type_user,
    }),
    before_session_not_started_connect: nonInteraction({
        title: "Пользователь кликнул на баннер 'Ваша сессия уже началась'",
        data: Attributes.type_user,
    }),
    lobby_camera_on: nonInteraction({
        title: "Пользователь включил камеру в лобби",
        data: Attributes.type_user,
    }),
    lobby_camera_off: nonInteraction({
        title: "Пользователь выключил камеру в лобби",
        data: Attributes.type_user,
    }),
    lobby_camera_change_opened: nonInteraction({
        title: "Пользователь открыл список камер в лобби",
        data: Attributes.type_user,
    }),
    lobby_camera_change_selected: nonInteraction({
        title: "Пользователь выбрал камеру в лобби",
        data: Attributes.type_user,
    }),
    lobby_mic_on: nonInteraction({
        title: "Пользователь включил микрофон в лобби",
        data: Attributes.type_user,
    }),
    lobby_mic_off: nonInteraction({
        title: "Пользователь выключил микрофон в лобби",
        data: Attributes.type_user,
    }),
    lobby_mic_change_opened: nonInteraction({
        title: "Пользователь открыл список микрофонов в лобби",
        data: Attributes.type_user,
    }),
    lobby_mic_change_selected: nonInteraction({
        title: "Пользователь выбрал микрофон в лобби",
        data: Attributes.type_user,
    }),
    call_camera_on: nonInteraction({
        title: "Пользователь включить камеру в комнате",
        data: Attributes.type_user,
    }),
    call_camera_off: nonInteraction({
        title: "Пользователь выключил камеру в комнате",
        data: Attributes.type_user,
    }),
    call_camera_change_opened: nonInteraction({
        title: "Пользователь открыл список камер в комнате",
        data: Attributes.type_user,
    }),
    call_camera_change_selected: nonInteraction({
        title: "Пользователь выбрал камеру в комнате",
        data: Attributes.type_user,
    }),
    call_mic_on: nonInteraction({
        title: "Пользователь включить микрофон в комнате",
        data: Attributes.type_user,
    }),
    call_mic_off: nonInteraction({
        title: "Пользователь выключил микрофон в комнате",
        data: Attributes.type_user,
    }),
    call_mic_change_opened: nonInteraction({
        title: "Пользователь открыл список микрофонов в комнате",
        data: Attributes.type_user,
    }),
    call_mic_change_selected: nonInteraction({
        title: "Пользователь выбрал микрофон в комнате",
        data: Attributes.type_user,
    }),
    call_end_call_opened: nonInteraction({
        title: "Пользователь открыл модальное окно выйти в комнате",
        data: Attributes.type_user,
    }),
    call_end_call_selected: nonInteraction({
        title: "Пользователь кликнул на кнопку выйти в комнате",
        data: Attributes.type_user,
    }),
    after_feedback_page_loaded: nonInteraction({
        title: "Страница обратная связь загружена",
        data: Attributes.type_user,
    }),
    after_feedback_started: nonInteraction({
        title: "Пользователь начал работать с формой обратной связи",
        data: Attributes.type_user,
    }),
    after_nextstep_loaded: nonInteraction({
        title: "Страница следущий щаг загружена",
        data: Attributes.type_user,
    }),
    after_nextstep_booknext: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Записаться на следующую'",
        data: Attributes.type_user,
    }),
    after_nextstep_later: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Позже'",
        data: Attributes.type_user,
    }),
    after_nextstep_viewcalendar: nonInteraction({
        title: "Пользователь кликнул на кнопку 'К моему расписанию'",
        data: Attributes.type_user,
    }),
    calendar_add_lk: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Добавить' в блоке 'Добавьте сессии в календарь' на странице Мой Alter",
    }),
    calendar_add_settings: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Создать ссылку' в блоке 'Добавьте сессии себе в календарь' на странице Настройки",
    }),
    share_link_from_success_booking: nonInteraction({
        title: "Пользователь кликнул на кнопку «Скопировать» в блоке 'Поделиться с близкими'",
    }),
    profile_video_opened: nonInteraction({
        title: "Пользователь запустил просмотр видео в профиле",
        data: Attributes.psychologist,
    }),
    card_shown: nonInteraction({
        title: "Клиент перешёл во вкладку по добавлению карт",
    }),
    card_no_sign: nonInteraction({
        title: "Клиенту ушёл не добавив карту",
    }),
    card_1st_success: nonInteraction({
        title: "Клиент сохранил 1-ую российскую карту успешно",
    }),
    card_more_success: nonInteraction({
        title: "Клиент сохранил ещё одну российскую карту - успешно",
    }),
    card_no_ru_card_try_save: nonInteraction({
        title: "Клиент пытался сохранить иностранную карту",
    }),
    card_remove: nonInteraction({
        title: "Клиент удалил карту",
    }),
    ref_main_clicked: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Получить скидку' в блоке 'Получите скидку' на странице Мой Alter",
    }),
    ref_lk_clicked: nonInteraction({
        title: "Пользователь кликнул на кнопку в блоке 'Получите скидку 10%' на странице Настройки",
    }),
    ref_promo_copied: nonInteraction({
        title: "Пользователь кликнул на кнопку 'Скопировать' в блоке 'Получите скидку 10%' на странице Настройки",
    }),
    send_manual_selection_request: nonInteraction({
        title: "Бронирование после выбора времени",
        data: Attributes.user_name_email_object,
    }),
    show_psy_after_form_result: nonInteraction({
        title: "Отправка формы с данным клиента после получения списка найденных по анкете психологов",
        data: Attributes.user_name_email_object,
    }),
    experiment_viewed: nonInteraction({
        title: "Аналитика от growthBook",
        data: [Attributes.experiment_id, Attributes.variation_id],
    }),
    lk_login_current: nonInteraction({
        title: "Клиент зашел на форму авторизации",
    }),
    lk_signup: nonInteraction({
        title: "Клиент зашел на форму регистрации",
    }),
} as unknown as Record<string, ReadyEventDesc>

Analytics = setUpAnalyticsObject(Analytics, Aggregates.user.data)

export function useAnalytics() {
    if (isBrowser()) return Analytics
}
